import * as Sentry from "@sentry/react";
import { getChunkErrorDetails, isChunkError } from "./chunkReloadGuard";

const SENSITIVE_QUERY_PARAMETER = /token|code|password|secret|key|email|phone/i;
const recentEvents = new Map();
const DEDUPE_WINDOW_MS = 60 * 1000;

export const getBuildId = () => {
  if (process.env.REACT_APP_BUILD_ID) return process.env.REACT_APP_BUILD_ID;
  if (typeof document === "undefined") return "unknown";

  const buildMeta = document.querySelector('meta[name="saleoid-build-id"]');
  if (buildMeta?.content && !buildMeta.content.startsWith("%"))
    return buildMeta.content;

  const mainScript = document.querySelector('script[src*="/static/js/main."]');
  return mainScript?.src?.split("/").pop() || "unknown";
};

export const getSafeUrl = (value) => {
  try {
    const url = new URL(value, window.location.origin);
    const queryKeys = [...url.searchParams.keys()];
    url.search = queryKeys.length
      ? `?${queryKeys
          .map(
            (key) =>
              `${encodeURIComponent(key)}=${SENSITIVE_QUERY_PARAMETER.test(key) ? "[redacted]" : "[present]"}`,
          )
          .join("&")}`
      : "";
    url.hash = "";
    return url.toString();
  } catch (error) {
    return "unknown";
  }
};

const getPageContext = () => {
  if (typeof window === "undefined") return {};
  const navigation = performance.getEntriesByType?.("navigation")?.[0];
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  return {
    url: getSafeUrl(window.location.href),
    path: window.location.pathname,
    visibility: document.visibilityState,
    online: navigator.onLine,
    page_age_ms: Math.round(performance.now()),
    navigation_type: navigation?.type || "unknown",
    connection: connection?.effectiveType || "unknown",
  };
};

const getFingerprint = (error) => {
  const chunk = getChunkErrorDetails(error);
  return isChunkError(error)
    ? `chunk:${chunk.chunkId || chunk.chunkUrl || chunk.message}`
    : `runtime:${error?.name || "Error"}:${error?.message || String(error)}`;
};

export const flushErrorReports = () =>
  Sentry.flush ? Sentry.flush(1200) : Promise.resolve(false);

export const captureAppError = (
  error,
  { source, errorInfo, userContext } = {},
) => {
  const fingerprint = getFingerprint(error);
  const capturedAt = recentEvents.get(fingerprint);
  if (capturedAt && Date.now() - capturedAt.at < DEDUPE_WINDOW_MS) {
    return capturedAt.eventId;
  }

  const chunk = getChunkErrorDetails(error);
  const kind = isChunkError(error) ? "chunk_load" : "runtime";
  let eventId;

  Sentry.withScope((scope) => {
    scope.setTag("error.kind", kind);
    scope.setTag("error.source", source || "unknown");
    scope.setTag("app.build", getBuildId());
    scope.setContext("page", getPageContext());

    if (chunk.chunkId || chunk.chunkUrl) {
      scope.setContext("chunk", {
        id: chunk.chunkId,
        url: chunk.chunkUrl ? getSafeUrl(chunk.chunkUrl) : null,
      });
    }
    if (errorInfo?.componentStack) {
      scope.setExtra(
        "react_component_stack",
        errorInfo.componentStack.slice(0, 5000),
      );
    }
    if (userContext?.userId) scope.setUser({ id: String(userContext.userId) });
    if (userContext?.teamId)
      scope.setTag("team.id", String(userContext.teamId));

    eventId = Sentry.captureException(error);
  });

  recentEvents.set(fingerprint, { at: Date.now(), eventId });
  return eventId;
};

export const sanitizeSentryEvent = (event) => {
  if (event?.request?.url) event.request.url = getSafeUrl(event.request.url);
  return event;
};
